/**
 * The glyph that stands in for a file with no picture, chosen from its MIME
 * type.
 *
 * Shared by the board's preview cell and the preview modal it opens, so the
 * thing a person clicks and the thing they land on are the same shape rather
 * than two independently-chosen icons for one file.
 */
import {
  FileAudioIcon,
  FileIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  FileVideoIcon,
  ImageIcon,
  type LucideIcon,
} from 'lucide-react';

/* eslint-disable i18next/no-literal-string -- MIME types, machine values rather than copy */
const SPREADSHEET_TYPES: ReadonlySet<string> = new Set([
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

/**
 * Prefix match, so an exact type (`application/pdf`) and a whole family
 * (`text/`) sit in one list. Order is not load-bearing here - no entry is a
 * prefix of another - because the types that would collide with a family are in
 * the exact-match set above, which `fileTypeIcon` checks first.
 */
const ICON_BY_MIME_PREFIX: ReadonlyArray<readonly [string, LucideIcon]> = [
  ['image/', ImageIcon],
  ['video/', FileVideoIcon],
  ['audio/', FileAudioIcon],
  ['application/pdf', FileTextIcon],
  ['text/', FileTextIcon],
];
/* eslint-enable i18next/no-literal-string */

export function fileTypeIcon(fileType: string): LucideIcon {
  if (SPREADSHEET_TYPES.has(fileType)) return FileSpreadsheetIcon;
  return ICON_BY_MIME_PREFIX.find(([prefix]) => fileType.startsWith(prefix))?.[1] ?? FileIcon;
}
